// game/rooms.ts — build a room from its ASCII template, seal the doors, detect
// the clear, pay the reward, and hand the player to the next room. Also owns the
// two non-combat room features: the timed vault (DESIGN §3) and the respite's
// furniture — Altar, Fountain, boss door.
//
// Tile legend (types.ts). Parser note: a marker may be written either as a single
// character ('G', 'E' — index assigned in reading order) or as the two-character
// form from the legend ('G0', 'E1' — explicit index, still one tile). Zone 1 uses
// the single-character form so every row is exactly one character per tile.
//
// LEGEND EXTENSION (Phase 5, flagged for the orchestrator — types.ts is frozen so
// its legend comment does not yet list these four):
//   'V' vault door      — already in the legend; now implemented (timed, one/zone)
//   'v' vault interior  — floor inside the alcove; marks the region that seals
//   'A' altar           — respite: buy a relic / food / potion
//   'N' fountain        — respite: +HP once per run
//   'S' boss door       — respite: the sealed stair down to the boss
// W/R/B/T/X remain floor until their zones arrive.

import type { Room, RoomTemplate, Vec } from '../types'
import { TUNING } from '../data/tuning'
import { drawSprite } from '../engine/sprites'
import { emit } from '../engine/particles'
import { TILE } from './world'
import { makeGenerator } from './generators'
import { meta } from './meta'
import { fireHook } from './relics'
import { runStats } from './runstats'
import type { Run } from './game'

export interface TilePos {
  tx: number
  ty: number
}

/** Seconds the vault stays lootable once the Warden steps inside (DESIGN §3). */
export const VAULT_GRAB_SECONDS = 10

/** How close the Warden must stand to press E on a respite fixture. */
const INTERACT_RANGE = 52

export class RoomState implements Room {
  template!: RoomTemplate
  index = 0
  cleared = false
  elapsed = 0
  isRespite = false
  isVaultOpen = false

  // --- respite furniture (null outside a respite)
  altar: TilePos | null = null
  fountain: TilePos | null = null
  bossDoor: TilePos | null = null
  /** DESIGN §3: one relic purchase per altar visit. */
  altarRelicBought = false

  // --- timed vault
  vaultDoors: TilePos[] = []
  vaultTiles: TilePos[] = []
  hasVault = false
  vaultSealed = false
  vaultEntered = false
  vaultTimer = 0
  /** seconds every surviving enemy has been unreachable (softlock backstop) */
  strandedFor = 0

  resetFor(template: RoomTemplate, index: number, isRespite: boolean): void {
    this.template = template
    this.index = index
    this.cleared = isRespite
    this.elapsed = 0
    this.isRespite = isRespite
    this.isVaultOpen = false
    this.altar = null
    this.fountain = null
    this.bossDoor = null
    this.altarRelicBought = false
    this.vaultDoors.length = 0
    this.vaultTiles.length = 0
    this.hasVault = false
    this.vaultSealed = false
    this.vaultEntered = false
    this.vaultTimer = 0
    this.strandedFor = 0
  }
}

const scratch: Vec = { x: 0, y: 0 }

export function buildRoom(run: Run, template: RoomTemplate, index: number, isRespite = false): void {
  const w = run.world
  w.load(template.map)

  const room = run.room
  room.resetFor(template, index, isRespite)

  run.doors.length = 0
  run.gates.length = 0
  run.gatesOpen = false
  run.clearAllEntities()

  const foodSpots: Vec[] = []
  const genSpots: { defId: string; x: number; y: number; slot: number }[] = []
  const patrolSpots: { defId: string; x: number; y: number }[] = []
  let keySpot: Vec | null = null
  let spawn: Vec | null = null
  let genSeen = 0
  let patrolSeen = 0

  for (let ty = 0; ty < w.h; ty++) {
    for (let tx = 0; tx < w.w; tx++) {
      const g = w.glyphAt(tx, ty)
      if (g === '#' || g === '.') continue
      const cx = tx * TILE + TILE / 2
      const cy = ty * TILE + TILE / 2
      const head = g[0]

      if (head === 'G') {
        const slot = g.length > 1 ? Number(g[1]) : genSeen
        genSeen++
        const defId = template.generators[slot] ?? template.generators[0]
        if (defId) genSpots.push({ defId, x: cx, y: cy, slot })
        continue
      }
      if (head === 'E') {
        const slot = g.length > 1 ? Number(g[1]) : patrolSeen
        patrolSeen++
        const defId = template.patrols[slot] ?? template.patrols[0]
        if (defId) patrolSpots.push({ defId, x: cx, y: cy })
        continue
      }

      switch (g) {
        case '@':
          spawn = { x: cx, y: cy }
          break
        case 'F':
          foodSpots.push({ x: cx, y: cy })
          break
        case 'P':
          run.spawnPickup({ kind: 'potion', pos: { x: cx, y: cy }, value: 1 })
          break
        case '$':
          run.spawnPickup({ kind: 'gold', pos: { x: cx, y: cy }, value: TUNING.economy.goldSmall })
          break
        case 'C':
          run.spawnPickup({ kind: 'gold', pos: { x: cx, y: cy }, value: TUNING.economy.goldSmall * 4 })
          break
        case 'K':
          keySpot = { x: cx, y: cy }
          break
        case 'D':
          run.doors.push({ tx, ty })
          w.setSolid(tx, ty, true)
          break
        case 'L':
          run.gates.push({ tx, ty })
          w.setSolid(tx, ty, true)
          break
        case 'V':
          room.vaultDoors.push({ tx, ty })
          room.hasVault = true
          w.setSolid(tx, ty, true) // opened below if the zone clock allows
          break
        case 'v':
          room.vaultTiles.push({ tx, ty })
          break
        case 'A':
          room.altar = { tx, ty }
          w.setSolid(tx, ty, true) // furniture, not floor
          break
        case 'N':
          room.fountain = { tx, ty }
          w.setSolid(tx, ty, true)
          break
        case 'S':
          room.bossDoor = { tx, ty }
          w.setSolid(tx, ty, true) // the stair is sealed until you walk into it
          break
        default:
          // W/R/B/T/X and anything unknown: floor for now.
          break
      }
    }
  }

  // A room born cleared (respites, the spurs hub) has open doors from the start.
  if (room.cleared) {
    for (const d of run.doors) w.setSolid(d.tx, d.ty, false)
  }

  if (!spawn) {
    w.findAnyOpen(run.rngObj, scratch, run.player.radius)
    spawn = { x: scratch.x, y: scratch.y }
  }
  run.player.pos.x = spawn.x
  run.player.pos.y = spawn.y
  run.player.vel.x = 0
  run.player.vel.y = 0

  w.bakeLayer(run.zone.palette)
  run.hash.configure(w.pixelW, w.pixelH)
  run.renderer.setWorldSize(w.pixelW, w.pixelH)
  run.renderer.snapCameraTo(spawn.x, spawn.y)
  w.updateFlow(spawn.x, spawn.y, 0, true)

  for (const gs of genSpots) {
    const def = run.generatorDef(gs.defId)
    if (!def) continue
    run.generators.push(makeGenerator(def, gs.x, gs.y, run.generators.length, run.zone.statScale.hp))
  }

  for (const ps of patrolSpots) {
    scratch.x = ps.x
    scratch.y = ps.y
    run.spawnEnemy(ps.defId, scratch)
  }

  // food: the template offers candidate slots, the zone config says how many land
  const [fmin, fmax] = run.zone.foodPerRoom
  const want = Math.min(foodSpots.length, run.rngObj.int(fmin, fmax))
  for (let i = foodSpots.length - 1; i > 0; i--) {
    const j = run.rngObj.int(0, i)
    const t = foodSpots[i]
    foodSpots[i] = foodSpots[j]
    foodSpots[j] = t
  }
  for (let i = 0; i < want; i++) {
    run.spawnPickup({ kind: 'food', pos: foodSpots[i], value: TUNING.player.foodHeal })
  }

  // The key: on a marked elite when a gate guards something (DESIGN §5 Zone 1),
  // otherwise lying in the open (DESIGN §3 allows both).
  if (keySpot) {
    const carrierId = template.patrols[0]
    if (run.gates.length > 0 && carrierId) {
      scratch.x = keySpot.x
      scratch.y = keySpot.y
      const e = run.spawnEnemy(carrierId, scratch, { elite: 'bloated' })
      if (e.alive) {
        e.tags.carriesKey = true
      } else {
        run.spawnPickup({ kind: 'key', pos: keySpot, value: 1 })
      }
    } else {
      run.spawnPickup({ kind: 'key', pos: keySpot, value: 1 })
    }
  }

  if (room.hasVault) stockVault(run, room)

  run.sfx('door')
  run.mechanic.onRoomStart(run, room)
}

// ---------------------------------------------------------------- timed vault

/** Gold + embers + a food cache, no enemies (DESIGN §3). Amounts from tuning. */
function stockVault(run: Run, room: RoomState): void {
  const tiles = room.vaultTiles
  if (tiles.length === 0) return
  const order: TilePos[] = []
  run.rngObj.shuffleInto(tiles, order)

  const piles = Math.min(4, Math.max(2, Math.floor(order.length / 5)))
  const per = Math.max(1, Math.round(TUNING.economy.vaultGold / piles))
  let placed = 0
  for (let i = 0; i < piles && placed < order.length; i++, placed++) {
    const t = order[placed]
    run.spawnPickupAt('gold', t.tx * TILE + TILE / 2, t.ty * TILE + TILE / 2, per)
  }
  const emberPiles = 2
  const emberPer = Math.max(1, Math.round(TUNING.economy.vaultEmbers / emberPiles))
  for (let i = 0; i < emberPiles && placed < order.length; i++, placed++) {
    const t = order[placed]
    run.spawnPickupAt('custom', t.tx * TILE + TILE / 2, t.ty * TILE + TILE / 2, emberPer, 'ember')
  }
  if (placed < order.length) {
    const t = order[placed]
    run.spawnPickupAt('food', t.tx * TILE + TILE / 2, t.ty * TILE + TILE / 2, TUNING.player.foodHeal)
  }
}

function playerInVault(run: Run, room: RoomState): boolean {
  const tx = Math.floor(run.player.pos.x / TILE)
  const ty = Math.floor(run.player.pos.y / TILE)
  for (let i = 0; i < room.vaultTiles.length; i++) {
    if (room.vaultTiles[i].tx === tx && room.vaultTiles[i].ty === ty) return true
  }
  for (let i = 0; i < room.vaultDoors.length; i++) {
    if (room.vaultDoors[i].tx === tx && room.vaultDoors[i].ty === ty) return true
  }
  return false
}

function inVaultRegion(room: RoomState, tx: number, ty: number): boolean {
  for (let i = 0; i < room.vaultTiles.length; i++) {
    if (room.vaultTiles[i].tx === tx && room.vaultTiles[i].ty === ty) return true
  }
  for (let i = 0; i < room.vaultDoors.length; i++) {
    if (room.vaultDoors[i].tx === tx && room.vaultDoors[i].ty === ty) return true
  }
  return false
}

function setVaultDoors(run: Run, room: RoomState, open: boolean): void {
  if (room.isVaultOpen === open) return
  room.isVaultOpen = open
  if (!open) {
    // Anything shut inside the alcove is unreachable forever — and a room whose
    // last live enemy is unreachable can never clear. Dissolve them with the door.
    for (const e of run.enemyPool.live) {
      if (!e.alive) continue
      if (inVaultRegion(room, Math.floor(e.pos.x / TILE), Math.floor(e.pos.y / TILE))) e.kill(true)
    }
  }
  for (const d of room.vaultDoors) run.world.setSolid(d.tx, d.ty, !open)
  run.world.updateFlow(run.player.pos.x, run.player.pos.y, 0, true)
}

/** Contents vanish; the alcove shuts — but never on top of the Warden. */
function sealVault(run: Run, room: RoomState): void {
  if (room.vaultSealed) return
  room.vaultSealed = true
  for (const t of room.vaultTiles) {
    scratch.x = t.tx * TILE + TILE / 2
    scratch.y = t.ty * TILE + TILE / 2
    run.removePickupsAt(scratch, TILE)
    emit(scratch.x, scratch.y, { count: 3, color: '#6a5f8a', speed: 40, life: 0.5, size: 3, shape: 1 })
  }
  run.sfx('door')
  run.bark('The vault takes back what you did not carry.')
}

function updateVault(run: Run, room: RoomState, dt: number): void {
  if (!room.hasVault) return

  if (room.vaultSealed) {
    // never trap the Warden: the door stays open while they are still inside
    setVaultDoors(run, room, playerInVault(run, room))
    return
  }

  const underClock = run.zoneTime < run.zone.vaultClockSeconds

  if (!room.vaultEntered) {
    setVaultDoors(run, room, underClock)
    if (!underClock) {
      room.vaultSealed = true
      room.isVaultOpen = false
      return
    }
    if (playerInVault(run, room)) {
      room.vaultEntered = true
      room.vaultTimer = VAULT_GRAB_SECONDS
      runStats.vaultsOpened++
      run.featTick('vaultOpened')
      run.sfx('door')
      run.shake(3, 0.3)
      run.bark('Ten seconds. Take what you can carry.')
    }
    return
  }

  room.vaultTimer -= dt
  if (room.vaultTimer <= 0) {
    room.vaultTimer = 0
    sealVault(run, room)
    setVaultDoors(run, room, playerInVault(run, room))
  }
}

// ---------------------------------------------------------------- update

export function updateRoom(run: Run, dt: number): void {
  const room = run.room
  room.elapsed += dt

  updateVault(run, room, dt)

  if (room.isRespite) {
    updateRespite(run)
    return
  }

  // Zone interactables (braziers, coffers…): the mechanic gets first claim on E
  // outside the respite (Phase 6 contract, ZoneMechanic.onInteract).
  if (run.interactPressed()) run.mechanic.onInteract?.(run)

  // a held key opens every gate in the room, once
  if (!run.gatesOpen && run.gates.length > 0 && run.player.keys > 0) {
    run.player.keys--
    run.gatesOpen = true
    for (const g of run.gates) run.world.setSolid(g.tx, g.ty, false)
    run.world.updateFlow(run.player.pos.x, run.player.pos.y, 0, true)
    run.sfx('door')
    run.shake(3, 0.3)
    run.bark('The gate yields.')
  }

  if (!room.cleared) {
    // A boss arena does not clear when its parts die — the Broodgate's maws are
    // generators and two of them are meant to be down. It clears when the boss
    // falls: content/bosses/*.ts calls Run.openDescent(), which cuts the stair
    // and hands this function the door tile to watch.
    if (run.isBossRoom) return
    let anyGen = false
    for (let i = 0; i < run.generators.length; i++) {
      if (run.generators[i].alive) {
        anyGen = true
        break
      }
    }
    // Gloomspawn never hold a room hostage (D-009): the clear condition counts
    // only real enemies; survivors of the trickle dissolve on clear.
    let realEnemies = 0
    let reachable = 0
    for (const e of run.enemyPool.live) {
      if (!e.alive || e.tags.gloomspawn) continue
      realEnemies++
      // Flow-reachability alone is the wrong stranded predicate (D-015): a body
      // can be BFS-reachable yet physically pinned. A long-stuck body counts as
      // unreachable so the backstop below can dissolve it.
      if (run.world.distanceAt(e.pos.x, e.pos.y) >= 0 && e.stuckT < 3) reachable++
    }

    // Softlock backstop (generalises D-009): with every generator down and every
    // gate open, a survivor the Warden physically cannot reach — shut in a sealed
    // vault, walled off by a zone mechanic — is a room that never closes. Give it
    // a few seconds to be a pathing hiccup, then dissolve it.
    if (!anyGen && realEnemies > 0 && reachable === 0 && (run.gates.length === 0 || run.gatesOpen)) {
      room.strandedFor += dt
      if (room.strandedFor > 3) {
        for (const e of run.enemyPool.live) if (e.alive && !e.tags.gloomspawn) e.kill(true)
        realEnemies = 0
      }
    } else {
      room.strandedFor = 0
    }

    if (!anyGen && realEnemies === 0) {
      for (const e of run.enemyPool.live) if (e.alive && e.tags.gloomspawn) e.kill(true)
      clearRoom(run)
    }
    return
  }

  // cleared: stepping into a door takes the Warden onward — unless the zone
  // vetoes the exit (tolls, Phase 6 contract ZoneMechanic.onDoorTouched).
  const tx = Math.floor(run.player.pos.x / TILE)
  const ty = Math.floor(run.player.pos.y / TILE)
  for (let i = 0; i < run.doors.length; i++) {
    if (run.doors[i].tx === tx && run.doors[i].ty === ty) {
      if (run.mechanic.onDoorTouched && run.mechanic.onDoorTouched(run) === false) return
      run.advanceRoom(i)
      return
    }
  }
}

// ---------------------------------------------------------------- respite

function near(run: Run, t: TilePos | null): boolean {
  if (!t) return false
  const dx = t.tx * TILE + TILE / 2 - run.player.pos.x
  const dy = t.ty * TILE + TILE / 2 - run.player.pos.y
  return dx * dx + dy * dy <= INTERACT_RANGE * INTERACT_RANGE
}

export function altarInRange(run: Run): boolean {
  return near(run, run.room.altar)
}

export function fountainInRange(run: Run): boolean {
  return near(run, run.room.fountain)
}

/**
 * The respite is safe by construction: nothing spawns, the Gloom is paused
 * (gloom.ts), and the only three things in the room are the Altar, the Fountain
 * and the stair down.
 */
function updateRespite(run: Run): void {
  if (run.interactPressed()) {
    if (altarInRange(run)) {
      run.openAltar()
      return
    }
    if (fountainInRange(run)) {
      run.drinkFountain()
      return
    }
  }
  const tx = Math.floor(run.player.pos.x / TILE)
  const ty = Math.floor(run.player.pos.y / TILE)
  // Spurs hub (D-021): the respite can carry ordinary doors — each leads to a
  // spur. Walking into one advances with that door's index.
  for (let i = 0; i < run.doors.length; i++) {
    if (run.doors[i].tx === tx && run.doors[i].ty === ty) {
      run.advanceRoom(i)
      return
    }
  }
  const door = run.room.bossDoor
  if (!door) return
  // The stair is solid, so "entering" it means standing in the tile below it.
  if (tx === door.tx && (ty === door.ty || ty === door.ty + 1)) run.enterBossDoor()
}

// ---------------------------------------------------------------- clear

function clearRoom(run: Run): void {
  const room = run.room
  room.cleared = true
  for (const d of run.doors) run.world.setSolid(d.tx, d.ty, false)
  run.world.updateFlow(run.player.pos.x, run.player.pos.y, 0, true)

  const [emin, emax] = TUNING.economy.roomClearEmbers
  const embers = run.rngObj.int(emin, emax)
  run.addEmbers(embers, 'rooms')
  const gold = TUNING.economy.goldSmall * run.rngObj.int(2, 4)
  if (run.world.findOpenNear(run.player.pos.x, run.player.pos.y, 40, 140, run.rngObj, scratch, 8)) {
    run.spawnPickup({ kind: 'gold', pos: { x: scratch.x, y: scratch.y }, value: gold })
  }
  meta.roomCleared()
  run.featTick('roomsCleared')
  fireHook('onRoomClear', run)
  run.sfx('door')
  run.shake(2, 0.35)
  run.bark(run.doors.length > 0 ? 'The way opens. Go down.' : 'Room quiet. For now.')
}

// ---------------------------------------------------------------- render

function fixtureLabel(
  ctx: CanvasRenderingContext2D,
  s: string,
  x: number,
  y: number,
  size: number,
  color: string,
): void {
  ctx.font = `700 ${size}px ui-monospace, "SF Mono", Menlo, monospace`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillStyle = '#000000'
  ctx.fillText(s, x + 1, y + 1)
  ctx.fillStyle = color
  ctx.fillText(s, x, y)
}

export function renderRoomOverlay(ctx: CanvasRenderingContext2D, run: Run): void {
  const room = run.room
  const cleared = room.cleared
  for (let i = 0; i < run.doors.length; i++) {
    const d = run.doors[i]
    drawSprite(ctx, cleared ? 'door_open' : 'door_closed', d.tx * TILE + TILE / 2, d.ty * TILE + TILE / 2)
  }
  if (!run.gatesOpen) {
    for (let i = 0; i < run.gates.length; i++) {
      const g = run.gates[i]
      drawSprite(ctx, 'gate_locked', g.tx * TILE + TILE / 2, g.ty * TILE + TILE / 2)
    }
  }

  if (room.hasVault) renderVault(ctx, run, room)
  if (room.isRespite) renderRespite(ctx, run, room)
}

function renderVault(ctx: CanvasRenderingContext2D, run: Run, room: RoomState): void {
  const t = run.time
  // floor of the alcove reads as treasure even before you are in it
  if (!room.vaultSealed) {
    ctx.globalAlpha = 0.1 + 0.05 * Math.sin(t * 2.2)
    ctx.fillStyle = '#e3b23c'
    for (const v of room.vaultTiles) ctx.fillRect(v.tx * TILE, v.ty * TILE, TILE, TILE)
    ctx.globalAlpha = 1
  }

  for (const d of room.vaultDoors) {
    const x = d.tx * TILE + TILE / 2
    const y = d.ty * TILE + TILE / 2
    const open = room.isVaultOpen
    ctx.fillStyle = room.vaultSealed ? '#3a3323' : open ? '#6b5320' : '#4a3c1c'
    ctx.fillRect(d.tx * TILE + 2, d.ty * TILE + 2, TILE - 4, TILE - 4)
    ctx.strokeStyle = room.vaultSealed ? '#5a4a2a' : '#e3b23c'
    ctx.lineWidth = 2
    ctx.strokeRect(d.tx * TILE + 2.5, d.ty * TILE + 2.5, TILE - 5, TILE - 5)
    if (!room.vaultSealed) {
      // subtle golden shimmer while the clock still allows it
      const sh = 0.35 + 0.3 * Math.sin(t * 3.4 + d.tx)
      ctx.globalAlpha = sh
      ctx.fillStyle = '#ffd98a'
      ctx.fillRect(d.tx * TILE + 6, d.ty * TILE + 6, TILE - 12, TILE - 12)
      ctx.globalAlpha = 1
    }

    const dist = Math.hypot(run.player.pos.x - x, run.player.pos.y - y)
    if (dist < 260 && !room.vaultEntered && !room.vaultSealed) {
      const left = Math.max(0, run.zone.vaultClockSeconds - run.zoneTime)
      fixtureLabel(ctx, `VAULT · ${left.toFixed(0)}s`, x, y - TILE, 11, '#ffd98a')
    }
  }

  if (room.vaultEntered && !room.vaultSealed) {
    const p = run.player.pos
    const left = room.vaultTimer
    fixtureLabel(ctx, left.toFixed(1), p.x, p.y - 44, 22, left < 3 ? '#ff6b6b' : '#ffd98a')
    fixtureLabel(ctx, 'GRAB WINDOW', p.x, p.y - 62, 10, '#e3b23c')
  }
}

function renderRespite(ctx: CanvasRenderingContext2D, run: Run, room: RoomState): void {
  const t = run.time

  if (room.altar) {
    const x = room.altar.tx * TILE + TILE / 2
    const y = room.altar.ty * TILE + TILE / 2
    ctx.fillStyle = '#2a2634'
    ctx.fillRect(x - 13, y - 6, 26, 18)
    ctx.fillStyle = '#3b3547'
    ctx.fillRect(x - 10, y - 14, 20, 10)
    const pulse = 0.55 + 0.45 * Math.sin(t * 2.4)
    ctx.globalAlpha = 0.5 * pulse
    ctx.fillStyle = '#ffb347'
    ctx.beginPath()
    ctx.arc(x, y - 16, 7 + pulse * 2, 0, Math.PI * 2)
    ctx.fill()
    ctx.globalAlpha = 1
    ctx.fillStyle = '#ffd98a'
    ctx.beginPath()
    ctx.arc(x, y - 16, 3.5, 0, Math.PI * 2)
    ctx.fill()
    if (altarInRange(run)) fixtureLabel(ctx, 'E — ALTAR', x, y - 34, 12, '#ffd98a')
    else fixtureLabel(ctx, 'ALTAR', x, y - 30, 10, 'rgba(232,220,191,0.5)')
  }

  if (room.fountain) {
    const x = room.fountain.tx * TILE + TILE / 2
    const y = room.fountain.ty * TILE + TILE / 2
    const spent = run.fountainUses <= 0
    ctx.fillStyle = '#26303a'
    ctx.beginPath()
    ctx.ellipse(x, y + 4, 15, 9, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = spent ? '#2b3239' : '#3f7f8f'
    ctx.beginPath()
    ctx.ellipse(x, y + 2, 11, 6, 0, 0, Math.PI * 2)
    ctx.fill()
    if (!spent) {
      ctx.globalAlpha = 0.35 + 0.25 * Math.sin(t * 3)
      ctx.fillStyle = '#8fe6ff'
      ctx.beginPath()
      ctx.ellipse(x, y + 2, 8, 4, 0, 0, Math.PI * 2)
      ctx.fill()
      ctx.globalAlpha = 1
    }
    if (fountainInRange(run)) {
      fixtureLabel(
        ctx,
        spent ? 'DRY' : `E — DRINK (+${TUNING.player.fountainHeal})`,
        x,
        y - 26,
        12,
        spent ? 'rgba(232,220,191,0.45)' : '#8fe6ff',
      )
    } else {
      fixtureLabel(ctx, spent ? 'FOUNTAIN — SPENT' : 'FOUNTAIN', x, y - 24, 10, 'rgba(232,220,191,0.5)')
    }
  }

  if (room.bossDoor) {
    const x = room.bossDoor.tx * TILE + TILE / 2
    const y = room.bossDoor.ty * TILE + TILE / 2
    ctx.fillStyle = '#14121c'
    ctx.fillRect(x - 15, y - 15, 30, 30)
    const pulse = 0.4 + 0.35 * Math.sin(t * 1.6)
    ctx.globalAlpha = pulse
    ctx.fillStyle = '#7a2ad8'
    ctx.fillRect(x - 11, y - 11, 22, 22)
    ctx.globalAlpha = 1
    ctx.strokeStyle = '#c07bff'
    ctx.lineWidth = 2
    ctx.strokeRect(x - 14.5, y - 14.5, 29, 29)
    const dist = Math.hypot(run.player.pos.x - x, run.player.pos.y - y)
    if (dist < 200) fixtureLabel(ctx, 'THE STAIR DOWN', x, y + 30, 12, '#c07bff')
  }
}
